import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useAppAuth } from '../hooks/useAppAuth';
import axios from 'axios';

const SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour

export const AuthTokenManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, logout } = useAppAuth();
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isLoggingOutRef = useRef(false);
  const [tokenReady, setTokenReady] = useState(false);

  // The interceptors below are registered once per session and read the
  // latest getAccessTokenSilently through this ref. Keeping it (and `user`)
  // out of the effect's dependencies matters: auth0-react replaces `user`
  // whenever a token refresh completes, and re-running the effect on that
  // would eject the interceptors and unmount the app mid-request, leaving a
  // request with no Authorization header and a 401 nobody handles.
  const getTokenRef = useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;

  const doLogout = useCallback(() => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    delete axios.defaults.headers.common['Authorization'];
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    // 1. Make sure we can get a token before rendering children. The header
    //    itself is attached per request (below) so it is always current.
    const configure = async () => {
      try {
        await getTokenRef.current();
        setTokenReady(true);
      } catch (error) {
        console.error('Failed to get access token:', error);
        doLogout();
      }
    };
    configure();

    // 2. Inactivity timer - resets on user activity
    const resetTimer = () => {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(doLogout, SESSION_TIMEOUT_MS);
    };
    resetTimer();

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;
    activityEvents.forEach(event => document.addEventListener(event, resetTimer));

    // Attach a fresh access token to every request. auth0-react serves this
    // from its cache and only uses the refresh token when the access token is
    // close to expiry, so an active session no longer gets logged out when
    // the original token's lifetime runs out. Also resets the inactivity timer.
    const requestInterceptorId = axios.interceptors.request.use(async (config) => {
      resetTimer();
      try {
        const token = await getTokenRef.current();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to refresh access token:', error);
        doLogout();
        // Don't send the request unauthenticated - we are logging out anyway.
        throw error;
      }
      return config;
    });

    // 3. Response interceptor: 401/403 → immediate logout
    const responseInterceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          doLogout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      clearTimeout(inactivityTimerRef.current);
      activityEvents.forEach(event => document.removeEventListener(event, resetTimer));
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
      setTokenReady(false);
    };
  }, [isAuthenticated, isLoading, doLogout]);

  if (!tokenReady) return null;

  return <>{children}</>;
};
